import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { collection, query, where, orderBy, onSnapshot, updateDoc, doc } from 'firebase/firestore'
import { db } from '../../firebase.config'
import { useAuth } from '../../context/AuthContext'
import AppLayout from '../../components/layout/AppLayout'
import LessonModal from '../../components/shared/LessonModal'

const STATUS_COLORS: Record<string, string> = { Open: '#dc2626', Reviewed: '#2563eb', Closed: '#16a34a' }
const SEVERITY_COLORS: Record<string, string> = { Low: '#3b82f6', Medium: '#f59e0b', High: '#f97316', Critical: '#dc2626' }

export default function LessonsPage() {
  const { orgId } = useAuth()
  const navigate = useNavigate()
  const [lessons, setLessons] = useState<any[]>([])
  const [filter, setFilter] = useState('All')
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)

  useEffect(() => {
    if (!orgId) return
    const unsub = onSnapshot(
      query(collection(db, 'organizations', orgId, 'lessons'), where('orgId', '==', orgId), orderBy('createdAt', 'desc')),
      snap => { setLessons(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(false) }
    )
    return () => unsub()
  }, [orgId])

  const filtered = lessons.filter(l => {
    if (filter !== 'All' && l.status !== filter) return false
    if (search && !l.title?.toLowerCase().includes(search.toLowerCase())) return false
    return true
  })

  const handleStatusUpdate = async (id: string, status: string) => {
    await updateDoc(doc(db, 'organizations', orgId, 'lessons', id), { status, updatedAt: new Date().toISOString() })
  }

  const formatDate = (iso: string) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''

  return (
    <AppLayout title="Lessons">
      <div className="mb-6 flex gap-4 items-center">
        <button onClick={() => setShowModal(true)}
          className="bg-accent text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-700">
          + New Lesson
        </button>
        <input
          type="text"
          placeholder="Search lessons..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="border border-gray-300 rounded-lg px-4 py-2 text-sm w-64"
        />
        <div className="flex gap-2">
          {['All', 'Open', 'Reviewed', 'Closed'].map(s => (
            <button key={s} onClick={() => setFilter(s)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium border ${filter === s ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300'}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-5 py-3 font-medium text-gray-600">Title</th>
              <th className="text-left px-5 py-3 font-medium text-gray-600">Category</th>
              <th className="text-left px-5 py-3 font-medium text-gray-600">Work Area</th>
              <th className="text-left px-5 py-3 font-medium text-gray-600">Severity</th>
              <th className="text-left px-5 py-3 font-medium text-gray-600">Status</th>
              <th className="text-left px-5 py-3 font-medium text-gray-600">Date</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(l => (
              <tr key={l.id} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => window.open(`/lessons/${l.id}`, '_blank')}>
                <td className="px-5 py-3 font-medium text-gray-900">{l.title}</td>
                <td className="px-5 py-3 text-gray-600">{l.category || '-'}</td>
                <td className="px-5 py-3 text-gray-600">{l.workArea || '-'}</td>
                <td className="px-5 py-3">
                  <span className="px-2 py-1 rounded text-xs font-medium text-white" style={{ backgroundColor: SEVERITY_COLORS[l.severity] || '#888' }}>
                    {l.severity || 'Low'}
                  </span>
                </td>
                <td className="px-5 py-3">
                  <select value={l.status || 'Open'} onChange={e => handleStatusUpdate(l.id, e.target.value)}
                    className="text-xs font-medium border rounded px-2 py-1"
                    style={{ color: STATUS_COLORS[l.status] || '#888', borderColor: STATUS_COLORS[l.status] || '#888' }}>
                    <option>Open</option><option>Reviewed</option><option>Closed</option>
                  </select>
                </td>
                <td className="px-5 py-3 text-gray-500">{formatDate(l.createdAt)}</td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-12 text-center text-gray-400">No lessons found</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {showModal && <LessonModal onClose={() => setShowModal(false)} onCreated={() => {}} />}
    </AppLayout>
  )
}